import {QueryClient} from '@tanstack/react-query';
import {PropsWithChildren, useCallback, useMemo} from 'react';

import {useReviewRealtimePresence} from '../../../../service/reviews/review-realtime-presence/review-realtime-presence-context';
import {
    applyReviewUpdateWithBroadcast,
    getValidatedBroadcastChanges,
} from '../../../../service/reviews/review-realtime-presence/review-realtime-update-helper';
import {fetchReviewItem, getReviewItemUrl} from '../../../../service/reviews/reviews';
import {FetchReviewsResultType, ReviewDataChildType, ReviewDataType} from '../../../../service/reviews/reviews-type';

import {ReviewsStateContext} from './reviews-state-context';
import {ReviewsStateContextType} from './reviews-state-type';

type ReviewsStateContextBaseType = Omit<
    ReviewsStateContextType,
    'updateReview' | 'refreshReview' | 'refreshReviewWithAnswer'
>;

type ReviewsStateRealtimeBridgePropsType = PropsWithChildren<{
    baseContextValue: ReviewsStateContextBaseType;
    patchReviewInCache: (reviewId: number, partialReviewData: Partial<ReviewDataType>) => void;
    findBrandIdForReview: (reviewId: number) => number | undefined;
    queryClient: QueryClient;
    queryKey: string;
}>;

export function ReviewsStateRealtimeBridge(props: ReviewsStateRealtimeBridgePropsType): JSX.Element {
    const {baseContextValue, children, findBrandIdForReview, patchReviewInCache, queryClient, queryKey} = props;
    const {publishReviewUpdate} = useReviewRealtimePresence();

    const updateReview = useCallback(
        (reviewId: number, partialReviewData: Partial<ReviewDataType>) => {
            applyReviewUpdateWithBroadcast({
                reviewId,
                partialReviewData,
                patchReviewInCache,
                findBrandIdForReview,
                publishReviewUpdate,
            });
        },
        [findBrandIdForReview, patchReviewInCache, publishReviewUpdate]
    );

    const refreshReview = useCallback(
        async (reviewId: number) => {
            const response = await fetchReviewItem(reviewId);

            queryClient.setQueryData([queryKey], (oldData?: FetchReviewsResultType) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    results: oldData.results.map((review) => {
                        return response.id === review.id ? response : review;
                    }),
                };
            });

            queryClient.setQueryData([getReviewItemUrl(reviewId)], response);

            const broadcastChanges = getValidatedBroadcastChanges({
                children: response.children,
                processStatus: response.processStatus,
            });

            if (!broadcastChanges) {
                return;
            }

            const brandId = findBrandIdForReview(reviewId);

            if (!brandId) {
                return;
            }

            publishReviewUpdate(reviewId, brandId, broadcastChanges);
        },
        [findBrandIdForReview, publishReviewUpdate, queryClient, queryKey]
    );

    const refreshReviewWithAnswer = useCallback(
        (answer: ReviewDataChildType, reviewId: number) => {
            updateReview(reviewId, {
                processStatus: 'processed',
                children: [answer],
            });
        },
        [updateReview]
    );

    const value = useMemo(
        (): ReviewsStateContextType => ({
            ...baseContextValue,
            updateReview,
            refreshReview,
            refreshReviewWithAnswer,
        }),
        [baseContextValue, refreshReview, refreshReviewWithAnswer, updateReview]
    );

    return <ReviewsStateContext.Provider value={value}>{children}</ReviewsStateContext.Provider>;
}
